import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Paper, 
  CircularProgress, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton
} from '@material-ui/core';
import { 
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon
} from '@material-ui/icons';
import firebase from "firebase/app";
import "firebase/database";
import moment from 'moment';
import TopMenu from './TopMenu';

const SubmissionsList = () => {
  const { examCode } = useParams();
  const history = useHistory();
  const [submissions, setSubmissions] = useState([]);
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch exam data
        const examRef = firebase.database().ref(`TestPapers/${examCode}`);
        const examSnapshot = await examRef.once('value');
        const examData = examSnapshot.val();

        if (!examData || !examData.isDynamic) {
          setError('Dynamic assessment not found');
          setLoading(false);
          return;
        }

        setExam(examData);

        // Fetch submissions for this exam
        const submissionsRef = firebase.database().ref(`Exam_results/${examCode}`);
        const submissionsSnapshot = await submissionsRef.once('value');
        const submissionsData = submissionsSnapshot.val();

        if (submissionsData) {
          const submissionsList = Object.keys(submissionsData)
            .filter(key => submissionsData[key].isDynamic) // Only include dynamic exam submissions
            .map(key => ({
              id: key,
              ...submissionsData[key]
            }));
          setSubmissions(submissionsList);
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to load submissions');
        setLoading(false);
      }
    };

    fetchData();
  }, [examCode]);

  const handleViewSubmission = (submissionId) => {
    history.push(`/submission-result/${examCode}/${submissionId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <TopMenu showHome showLogout />
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => history.goBack()} style={{ marginRight: 16 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{exam.examTitle} - Submissions</Typography>
      </Box>

      <Paper elevation={2} style={{ padding: 24, marginBottom: 24 }}>
        <Typography variant="h6" gutterBottom>Assessment Details</Typography>
        <Typography variant="body1">Code: {examCode}</Typography>
        <Typography variant="body1">Timer: {exam.examTimer} minutes</Typography>
        <Typography variant="body1">Total Submissions: {submissions.length}</Typography>
      </Paper>

      {submissions.length === 0 ? (
        <Paper elevation={1} style={{ padding: 48, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No submissions found for this assessment.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Candidates will appear here once they complete the assessment.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Submitted At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((submission) => (
                <TableRow key={submission.id} hover>
                  <TableCell>
                    <Typography variant="body1">
                      {submission.student?.name || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {submission.student?.email || 'No email'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {moment(submission.submittedAt).format('MMM Do YYYY, h:mm a')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label="Completed" 
                      color="primary" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewSubmission(submission.id)}
                    >
                      View Results
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default SubmissionsList; 